import { GridContainer, Image, ImageRatioOptions, UeLightbox } from '../';
import { WithCaption, WithCaptionProps } from '../../wraps';
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

const ArticleImageWrapper = styled.div`
    margin: 0 auto;

    &:not(.article-image-contain) {
        position: relative;
        max-width: 1088px;
        .image-wrapper,
        img {
            max-height: initial;
            max-width: 1088px;
        }
        .lightbox-open {
            .image-wrapper,
            img {
                max-width: initial;
            }
        }
    }

    &.article-image-contain {
        position: relative;
        max-width: ${(p) => p.theme.widths.article}px;
        .image-wrapper {
            max-width: ${(p) => p.theme.widths.article}px;
            margin: 0 auto;
        }

        .lightbox-open {
            .image-wrapper,
            img {
                max-width: 70vw;
                max-height: 80vh;
            }
        }
    }

    .lightbox-link {
        cursor: pointer;
    }

    .caption-gradient {
        background: none;
    }

    &.ratio-9by16 {
        .lightbox-open {
            .image-wrapper,
            img {
                max-height: 80vh;
            }
        }
    }

    &.ratio-9by16, &.ratio-3by4 {
        max-width: 700px;
    }
`;

export interface ArticleImageProps {
    imageSrc?: string;
    ratio?: '1by1' | '16by9' | '21by9' | '9by16' | '4by3' | '3by4';
    contain?: boolean;
    caption?: WithCaptionProps;
    alignment?: string;
    alt?: string;
    forceCaptionBelow?: boolean;
}

export const ArticleImage = ({
    imageSrc,
    ratio = '16by9',
    caption,
    alt = '',
    alignment = 'center',
    contain = true,
    forceCaptionBelow = false
}: ArticleImageProps): JSX.Element => {
    const [isOpenImg, setIsOpenImg] = useState(false);

    if (forceCaptionBelow && caption) {
        caption.forceBelow = true;
    }

    let fit = 'cover';
    if (ratio === '9by16') {
        fit = 'contain';
    }

    let disableRatioHelper = false;
    if (ratio === '9by16' || ratio === '21by9' || ratio === '4by3' || ratio === '3by4') {
        disableRatioHelper = true;
    }

    let shape: ImageRatioOptions = 'custom';
    if (ratio === '16by9') {
        shape = 'rect';
    } else if (ratio === '1by1') {
        shape = 'square';
    }

    const ueLinkCallbackImg = useCallback(
        (e) => {
            setIsOpenImg(!isOpenImg);
        },
        [setIsOpenImg, isOpenImg]
    );

    return (
        <GridContainer>
            <ArticleImageWrapper
                className={`article-image-wrapper ratio-${ratio} ${contain ? 'article-image-contain' : ''}`}>
                {imageSrc && (
                    <>
                        <WithCaption {...caption} tinyCaption={false}>
                            <span
                                role="presentation"
                                className="lightbox-link"
                                onClick={ueLinkCallbackImg}>
                                <Image
                                    src={imageSrc}
                                    ratio={shape}
                                    alt={alt}
                                    alignment={alignment}
                                    disableRatioHelper={disableRatioHelper}
                                    fit={fit}
                                />
                            </span>
                        </WithCaption>
                    </>
                )}

                <UeLightbox isOpen={isOpenImg} onClick={ueLinkCallbackImg}>
                    <Image
                        src={imageSrc}
                        ratio={shape}
                        alt={alt}
                        disableRatioHelper={disableRatioHelper}
                        fit={'contain'}
                    />
                </UeLightbox>
            </ArticleImageWrapper>
        </GridContainer>
    );
};
